import { SelectionModel } from '@angular/cdk/collections';
import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, DestroyRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatSort } from '@angular/material/sort';
import { Router, RouterModule } from '@angular/router';
import { LoaderService } from '@fdm/services';
import {
  ApprovalFlowStateOptions,
  consequenceRankingOptions,
  DisciplineOptions,
  FacilityEnumOptions,
  FDMStatusOptions,
  FFSTypeOptions,
  ReportTypeOptions,
  utcToLocal
} from '@fdm/utils';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { distinctUntilChanged, finalize, first, firstValueFrom, map, merge, of, Subject, switchMap, timer } from 'rxjs';
import {
  ApprovalState,
  Client,
  DamageMechanismDto,
  EquipmentSafeguard,
  ReportType,
  FFSType,
  GeneralTabSearchRequest as ApiGeneralTabSearchRequest,
} from '../../../api/api-clientInformation';
import { MaterialModule } from '../../../material/material.module';
import { CommonUserService } from '../../../services/common-user.service';
import { SearchStorageService } from '../../../services/search-storage.service';
import { CurrentUserService } from '../../../services/current-user.service';
import { FilterResponse, FiltrationType, TableColumnFilterComponent, TableColumnFilterOption, TableColumnFilterType, ToolbarComponent } from '../../../shared';
import { ColumnResizeDirective } from '../../../shared/directives';
import { GENERAL_TAB_LABELS } from '../../../utils/column-labels';
import { approvalStatusNaming } from '../../../utils/approval-status-naming';
import { ApprovalStatusNamingPipe } from '../../pipes';
import { ApprovalFlowService, ButtonScriptsService, ReportFormStore } from '../../services';
import { PolicyService } from '../../services/roles/policy.service';


const DEFAULT_PAGES = [25, 50, 75, 100];
const DRAFT_STATUSES: ApprovalState[] = [ApprovalState.Draft, ApprovalState.DraftedForReview];

// Sent as an "equals" value for a coded column when the user's typed text matches no known option,
// guaranteeing an empty result set (nothing can equal this token).
const NO_MATCH_SENTINEL = '\u0000__no_match__';

enum FDMRecordStatus {
  ACTIVE_DEFICIENCY,
  ACTIVE_TASKDEFERRAL,
  ACTIVE_RELIABILITY,
  ACTIVE_INTEGRITY,
  ACTIVE_ANOMALY,
  ACTIVE_ALL,
  INACTIVE_ALL,
}

interface GeneralTabRecord {
  id?: string;
  uniqueId?: string | undefined;
  reportType?: ReportType;
  reportTitleEn?: string | undefined;
  discipline?: string | undefined;
  waitingFor?: string | undefined;
  resolutionTargetDate?: Date | undefined;
  initiator?: string | undefined;
  closedOn?: Date | undefined;
  riptSeriatimRank?: number | undefined;
  facility?: string | undefined;
  abcPrioritization?: string | undefined;
  consequenceRanking?: string | undefined;
  createdOn?: Date | undefined;
  planApprovedDate?: Date | undefined;
  equipment?: string | undefined;
  isItSafeguard?: string | undefined;
  riskEvaluationScore?: string | number | undefined;
  planApprovalDueDate?: Date | undefined;
  daysForApproval?: number | undefined;
  owner?: string | undefined;
  taRequired?: string | undefined;
  ffsPerformed?: string | undefined;
  tlrReference?: string | undefined;
  tlrReferenceLink?: string | undefined;
  woReference?: string | undefined;
  woReferenceLink?: string | undefined;
  mocReference?: string | undefined;
  mocReferenceLink?: string | undefined;
  dewItReference?: string | undefined;
  dewItReferenceLink?: string | undefined;
  damageMechanismsFailureMode?: string | undefined;
  formattedReportType: string | null;
  status?: string;
  step?: ApprovalState;
}

type BackendStatusFilterMode = 'Include' | 'Exclude';
type BackendSortDirection = 'Asc' | 'Desc';
type BackendSortField =
  | 'CreatedOn'
  | 'UniqueId'
  | 'ReportType'
  | 'ReportTitleEn'
  | 'Status'
  | 'Discipline'
  | 'WaitingFor'
  | 'ResolutionTargetDate'
  | 'Initiator'
  | 'ClosedOn'
  | 'Facility'
  | 'ABCPrioritization'
  | 'ConsequenceRanking'
  | 'RiptSeriatimRank'
  | 'RiskEvaluationScore'
  | 'PlanApprovedDate'
  | 'PlanApprovalDueDate'
  | 'DaysUntilApprovalDeadline'
  | 'Owner'
  | 'Equipment'
  | 'IsSafeguard'
  | 'DamageMechanisms'
  | 'TaRequired'
  | 'FfsPerformed'
  | 'TlrReference'
  | 'WoReference'
  | 'MocReference'
  | 'DewItReference';

type BackendFilterOperator =
  | 'eq'
  | 'neq'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'contains'
  | 'ncontains'
  | 'startswith'
  | 'nstartswith'
  | 'endswith'
  | 'nendswith'
  | 'isnull'
  | 'isnotnull';

interface BackendColumnFilter {
  field: BackendSortField;
  operator: BackendFilterOperator;
  value?: string;
}

interface GeneralTabSearchFilterDto {
  reportType?: ReportType;
  search?: string;
  statuses?: ApprovalState[];
  statusFilterMode?: BackendStatusFilterMode;
  sortBy?: BackendSortField;
  sortDirection?: BackendSortDirection;
  columnFilters?: BackendColumnFilter[];
}

interface GeneralTabSearchRequest {
  pageNumber: number;
  pageSize: number;
  filter: GeneralTabSearchFilterDto;
}

@Component({
  selector: 'app-reports-list',
  templateUrl: './reports-list.component.html',
  styleUrl: './reports-list.component.scss',
  imports: [
    CommonModule,
    MaterialModule,
    ReactiveFormsModule,
    // Components
    ToolbarComponent,
    RouterModule,
    ApprovalStatusNamingPipe,
    ColumnResizeDirective,
    TableColumnFilterComponent
  ]
})

export class ReportsListComponent implements OnInit, AfterViewInit {
  Math = Math;
  private readonly COMPONENT_NAME = 'reports-list';

  displayedColumns: string[] =
    [ 'uniqueId',
      'formattedReportType',
      'reportTitleEn',
      'facility',
      'equipment',
      'isItSafeguard',
      'damageMechanismsFailureMode',
      'discipline',
      'consequenceRanking',
      'status',
      'step',
      'abcPrioritization',
      'riptSeriatimRank',
      'riskEvaluationScore',
      'createdOn',
      'planApprovalDueDate',
      'daysForApproval',
      'planApprovedDate',
      'resolutionTargetDate',
      'closedOn',
      'waitingFor',
      'initiator',
      'owner',
      'taRequired',
      'ffsPerformed',
      'tlrReference',
      'woReference',
      'mocReference',
      'dewItReference'
    ];

  columnLabels = GENERAL_TAB_LABELS;
  dataSource: GeneralTabRecord[] = [];
  filteredData: GeneralTabRecord[] = [];
  paginatedData: GeneralTabRecord[] = [];
  selection = new SelectionModel<GeneralTabRecord>(true, []);
  @ViewChild(MatSort) sort!: MatSort;

  control = new FormControl<string | null>(null);

  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private apiClient = inject(Client);
  private router = inject(Router);
  private store = inject(ReportFormStore);
  private approvalFlow = inject(ApprovalFlowService);
  private searchStorage = inject(SearchStorageService);
  private currentUserService = inject(CurrentUserService);
  private commonUserService = inject(CommonUserService);

  private _loaderService = inject(LoaderService);
  private _toasterService = inject(ToastrService);
  protected buttonScripts = inject(ButtonScriptsService);
  public canCreate$ = inject(PolicyService).canCreate$;

  pageIndex: number = 0;
  pageSize: number = 25;
  totalPageCount: number = 100;

  isProcessing = this._loaderService.processing$;
  isLoading = this._loaderService.loading$;

  statusFilter = signal<FDMRecordStatus>(FDMRecordStatus.ACTIVE_ALL);

  private currentSortActive: string = '';
  private currentSortDirection: 'asc' | 'desc' | '' = '';
  public showDraftedOnly = false;
  public showWaitingForOnly = false;
  public filtersResetVersion = 0;
  private searchReset$ = new Subject<string>();
  private waitingForAssigneeEmails: string[] = [];
  private waitingForEmailsKey = '';
  private damageMechanismOptions: TableColumnFilterOption[] = [];

  pageSizeOptions = DEFAULT_PAGES;
  isDisabled = () => this.selection.selected.length === 0;

  cloneReport() {
    const selectedReports = this.selection.selected;

    if (selectedReports.length === 0) {
      this._toasterService.error('No reports selected for cloning.');
      return;
    }

    this._loaderService.startProcessing();

    const cloneRequests = selectedReports.map(async report => {
      try {
        await firstValueFrom(this.apiClient.clone(report.id!).pipe(first()));
        this._toasterService.success(`Report "${report.reportTitleEn}" cloned successfully.`);
      } catch (err: any) {
        let errorMessage = err.errors?.[0] || err.message || 'Unknown error';
        console.error(errorMessage);
        if (errorMessage.includes('The service is unavailable.')) {
          this._toasterService.error(
            `Error cloning the report "${report.reportTitleEn}": ${errorMessage}`,
            'Cloning Failed',
            { timeOut: 12000 }
          );
        }
        else {
          errorMessage = 'An error occurred while cloning. Please fill in the required field. Please try again.';
          this._toasterService.error(
            `Error cloning the report "${report.reportTitleEn}": ${errorMessage}`,
            'Cloning Failed',
            { timeOut: 12000 }
          );

        }

      }
    });

    Promise.allSettled(cloneRequests)
      .finally(() => {
        this._loaderService.stopProcessing();
        this.selection.clear();
        this.loadReports();
      });
  }

  ngOnInit(): void {
    this.loadSearchFromStorage();
    this.setupWaitingForEmails();
    this.loadDamageMechanismOptions();
    const savedSearch = this.control.value || '';
    this.loadReports(savedSearch.trim());
    this.setupSearchFilter();
  }

  private setupWaitingForEmails(): void {
    this.commonUserService.userEmails$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(emails => {
        const normalized = Array.from(new Set((emails ?? [])
          .map(email => (email ?? '').trim().toLowerCase())
          .filter(Boolean)));

        const authEmail = this.currentUserService.getCurrentEmail()?.trim().toLowerCase();
        if (authEmail && !normalized.includes(authEmail)) {
          normalized.push(authEmail);
        }

        this.waitingForAssigneeEmails = normalized;
        const nextKey = normalized.join(',');
        const changed = nextKey !== this.waitingForEmailsKey;
        this.waitingForEmailsKey = nextKey;

        if (this.showWaitingForOnly && changed && normalized.length > 0) {
          this.pageIndex = 0;
          this.loadReports(this.control.value?.trim());
        }
      });
  }

  private loadDamageMechanismOptions(): void {
    this.apiClient.damageMechanismAll()
      .pipe(first())
      .subscribe({
        next: (items: DamageMechanismDto[]) => {
          this.damageMechanismOptions = items
            .filter(item => item.status === 'Active')
            .map(item => {
              const title = (item.title ?? item.nameEn ?? item.code ?? '').trim();
              return { label: title, value: title };
            })
            .filter(item => item.value.length > 0);
        },
        error: () => {
          this.damageMechanismOptions = [];
        }
      });
  }

  ngAfterViewInit(): void {
    if (this.sort) {
      this.sort.sortChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        this.currentSortActive = this.sort.active;
        this.currentSortDirection = this.sort.direction;

        // Reset to first page
        this.pageIndex = 0;

        // Fetch all data when sorting is activated
        const currentSearch = this.control.value || '';
        this.loadReports(currentSearch.trim());
      });
    }
  }

  private applySorting(): void {
    // sorting is handled server-side
  }

  private getStatusesFilter(): ApprovalState[] | undefined {
    if (this.showWaitingForOnly) {
      return undefined;
    }

    return DRAFT_STATUSES;
  }

  private getStatusFilterMode(): BackendStatusFilterMode | undefined {
    if (this.showWaitingForOnly) {
      return undefined;
    }

    return this.showDraftedOnly ? 'Include' : 'Exclude';
  }

  private loadSearchFromStorage(): void {
    const savedSearch = this.searchStorage.getSearch(this.COMPONENT_NAME);
    if (savedSearch) {
      this.control.setValue(savedSearch, { emitEvent: false });
    }
  }

  private setupSearchFilter(): void {
    merge(
      this.control.valueChanges.pipe(
        map(searchText => ({ searchValue: searchText || '', immediate: false }))
      ),
      this.searchReset$.pipe(
        map(searchValue => ({ searchValue, immediate: true }))
      )
    ).pipe(
      // Update the last distinct value before any debounce delay.
      distinctUntilChanged((prev, curr) => !curr.immediate && prev.searchValue === curr.searchValue),
      switchMap(({ searchValue, immediate }) => {
        if (immediate) {
          return of(searchValue);
        }

        return timer(300).pipe(map(() => searchValue));
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(searchValue => {
      // Save to localStorage via service
      this.searchStorage.saveSearch(this.COMPONENT_NAME, searchValue);

      // Reset to first page when search changes
      this.pageIndex = 0;

      // Reload data with appropriate pagination based on search
      this.loadReports(searchValue.trim());
    });
  }



  private mapSortField(column: string): BackendSortField | undefined {
    const map: Record<string, BackendSortField> = {
      uniqueId: 'UniqueId',
      formattedReportType: 'ReportType',
      reportType: 'ReportType',
      reportTitleEn: 'ReportTitleEn',
      status: 'Status',
      step: 'Status',
      discipline: 'Discipline',
      waitingFor: 'WaitingFor',
      resolutionTargetDate: 'ResolutionTargetDate',
      initiator: 'Initiator',
      closedOn: 'ClosedOn',
      facility: 'Facility',
      abcPrioritization: 'ABCPrioritization',
      consequenceRanking: 'ConsequenceRanking',
      riptSeriatimRank: 'RiptSeriatimRank',
      riskEvaluationScore: 'RiskEvaluationScore',
      createdOn: 'CreatedOn',
      planApprovedDate: 'PlanApprovedDate',
      planApprovalDueDate: 'PlanApprovalDueDate',
      daysForApproval: 'DaysUntilApprovalDeadline',
      owner: 'Owner',
      equipment: 'Equipment',
      isItSafeguard: 'IsSafeguard',
      damageMechanismsFailureMode: 'DamageMechanisms',
      taRequired: 'TaRequired',
      ffsPerformed: 'FfsPerformed',
      tlrReference: 'TlrReference',
      woReference: 'WoReference',
      mocReference: 'MocReference',
      dewItReference: 'DewItReference',
    };

    return map[column];
  }

  isSortableColumn(column: string): boolean {
    return !!this.mapSortField(column);
  }

  isServerFilterableColumn(column: string): boolean {
    return !!this.mapSortField(column);
  }

  getFilterType(column: string): TableColumnFilterType {
    const multiSelectColumns = new Set([
      'formattedReportType',
      'reportType',
      'facility',
      'status',
      'step',
      'discipline',
      'abcPrioritization',
      'consequenceRanking',
      'damageMechanismsFailureMode',
      'isItSafeguard',
      'taRequired',
      'ffsPerformed',
    ]);

    const numericColumns = new Set(['riptSeriatimRank', 'riskEvaluationScore', 'daysForApproval']);

    if (this.isDateColumn(column)) {
      return 'date';
    }

    if (numericColumns.has(column)) {
      return 'number';
    }

    if (multiSelectColumns.has(column)) {
      return 'multi-select';
    }

    return 'string';
  }

  getFilterOptions(column: string): TableColumnFilterOption[] {
    switch (column) {
      case 'formattedReportType':
      case 'reportType':
        return ReportTypeOptions.map(item => ({ label: item.label, value: `${item.value}` }));
      case 'facility':
        return FacilityEnumOptions.map(item => ({ label: item.label, value: `${item.value}` }));
      case 'status':
        return this.statusGroupOptions().map(item => ({ label: item.label, value: item.codes.join(',') }));
      case 'step':
        return ApprovalFlowStateOptions.map(item => ({ label: item.label, value: `${item.value}` }));
      case 'discipline':
        return DisciplineOptions
          .filter(item => !!item.title)
          .map(item => ({ label: item.title as string, value: item.title as string }));
      case 'abcPrioritization':
        return ['A', 'B', 'C', 'D'].map(item => ({ label: item, value: item }));
      case 'consequenceRanking':
        return consequenceRankingOptions.map(item => ({ label: item.label, value: `${item.value}` }));
      case 'damageMechanismsFailureMode':
        return this.damageMechanismOptions;
      case 'isItSafeguard':
        return this.getEnumStringOptions(EquipmentSafeguard);
      case 'taRequired':
        return [
          { label: 'Yes', value: 'true' },
          { label: 'No', value: 'false' },
        ];
      case 'ffsPerformed':
        return FFSTypeOptions
          .filter(item => item.value !== undefined)
          .map(item => ({ label: item.label, value: `${item.value}` }));
      default:
        return [];
    }
  }

  private getSortDirection(): BackendSortDirection {
    return this.currentSortDirection === 'asc' ? 'Asc' : 'Desc';
  }

  private getEnumStringOptions(enumObj: Record<string, string | number>): TableColumnFilterOption[] {
    return Object.keys(enumObj)
      .filter(key => Number.isNaN(Number(key)))
      .map(key => ({ label: key, value: key }));
  }

  private asYesNo(value: unknown): string | undefined {
    if (value === null || value === undefined || value === '') return undefined;
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (`${value}`.toLowerCase() === 'true') return 'Yes';
    if (`${value}`.toLowerCase() === 'false') return 'No';
    return `${value}`;
  }

  private formatEquipmentSafeguard(value: unknown): string | undefined {
    if (value === null || value === undefined || value === '') return undefined;

    const normalized = `${value}`.trim().toLowerCase();

    if (normalized === `${EquipmentSafeguard.Unknown}` || normalized === 'unknown') return 'Unknown';
    if (normalized === `${EquipmentSafeguard.Yes}` || normalized === 'yes' || normalized === 'true') return 'Yes';
    if (normalized === `${EquipmentSafeguard.No}` || normalized === 'no' || normalized === 'false') return 'No';

    return `${value}`;
  }

  private formatFfsPerformed(value: unknown): string | undefined {
    if (value === null || value === undefined || value === '') return undefined;
    const raw = `${value}` as FFSType;
    return FFSTypeOptions.find(o => `${o.value}` === raw)?.label ?? raw;
  }

  private formatDateStart(date: moment.Moment): string {
    return date.startOf('day').toISOString();
  }

  private formatDateEnd(date: moment.Moment): string {
    return date.endOf('day').toISOString();
  }

  private mapFilterToBackend(field: BackendSortField, filter: FilterResponse): BackendColumnFilter[] {
    const value = filter?.value;
    const valueTo = filter?.valueTo;
    const selectedValues = Array.isArray(filter?.values)
      ? filter.values.map(item => `${item}`).filter(item => item.length > 0)
      : [];

    if (selectedValues.length > 0) {
      if (filter.filter === FiltrationType.DOES_NOT_EQUAL) {
        return [{ field, operator: 'neq', value: selectedValues.join(',') }];
      }

      if (filter.filter === FiltrationType.EQUALS) {
        return [{ field, operator: 'eq', value: selectedValues.join(',') }];
      }
    }

    const hasValue = value !== null && value !== undefined && `${value}`.trim() !== '';
    const hasValueTo = valueTo !== null && valueTo !== undefined && `${valueTo}`.trim() !== '';
    const dateMoment = hasValue ? moment(value as any) : null;
    const dateMomentTo = hasValueTo ? moment(valueTo as any) : null;
    const numberValue = hasValue ? Number(value) : NaN;
    const numberValueTo = hasValueTo ? Number(valueTo) : NaN;

    switch (filter.filter) {
      case FiltrationType.EQUALS:
        return hasValue ? [{ field, operator: 'eq', value: `${value}` }] : [];
      case FiltrationType.DOES_NOT_EQUAL:
        return hasValue ? [{ field, operator: 'neq', value: `${value}` }] : [];
      case FiltrationType.CONTAINS_DATA:
        return [{ field, operator: 'isnotnull' }];
      case FiltrationType.DOES_NOT_CONTAIN_DATA:
        return [{ field, operator: 'isnull' }];
      case FiltrationType.CONTAINS:
        return hasValue ? [{ field, operator: 'contains', value: `${value}` }] : [];
      case FiltrationType.DOES_NOT_CONTAIN:
        return hasValue ? [{ field, operator: 'ncontains', value: `${value}` }] : [];
      case FiltrationType.BEGINS_WITH:
        return hasValue ? [{ field, operator: 'startswith', value: `${value}` }] : [];
      case FiltrationType.DOES_NOT_BEGIN_WITH:
        return hasValue ? [{ field, operator: 'nstartswith', value: `${value}` }] : [];
      case FiltrationType.ENDS_WITH:
        return hasValue ? [{ field, operator: 'endswith', value: `${value}` }] : [];
      case FiltrationType.DOES_NOT_END_WITH:
        return hasValue ? [{ field, operator: 'nendswith', value: `${value}` }] : [];

      case FiltrationType.ON:
        if (!dateMoment?.isValid()) return [];
        return [
          { field, operator: 'gte', value: this.formatDateStart(dateMoment.clone()) },
          { field, operator: 'lte', value: this.formatDateEnd(dateMoment.clone()) }
        ];
      case FiltrationType.ON_OR_AFTER:
        return dateMoment?.isValid() ? [{ field, operator: 'gte', value: this.formatDateStart(dateMoment.clone()) }] : [];
      case FiltrationType.ON_OR_BEFORE:
        return dateMoment?.isValid() ? [{ field, operator: 'lte', value: this.formatDateEnd(dateMoment.clone()) }] : [];
      case FiltrationType.BETWEEN:
        if (filter.type === 'date') {
          if (!dateMoment?.isValid() || !dateMomentTo?.isValid()) return [];

          const from = moment.min(dateMoment.clone(), dateMomentTo.clone());
          const to = moment.max(dateMoment.clone(), dateMomentTo.clone());

          return [
            { field, operator: 'gte', value: this.formatDateStart(from) },
            { field, operator: 'lte', value: this.formatDateEnd(to) }
          ];
        }

        if (!Number.isFinite(numberValue) || !Number.isFinite(numberValueTo)) return [];

        return [
          { field, operator: 'gte', value: `${Math.min(numberValue, numberValueTo)}` },
          { field, operator: 'lte', value: `${Math.max(numberValue, numberValueTo)}` }
        ];
      case FiltrationType.GREATER_THAN:
        return Number.isFinite(numberValue) ? [{ field, operator: 'gt', value: `${numberValue}` }] : [];
      case FiltrationType.GREATER_OR_EQUAL:
        return Number.isFinite(numberValue) ? [{ field, operator: 'gte', value: `${numberValue}` }] : [];
      case FiltrationType.LESS_THAN:
        return Number.isFinite(numberValue) ? [{ field, operator: 'lt', value: `${numberValue}` }] : [];
      case FiltrationType.LESS_OR_EQUAL:
        return Number.isFinite(numberValue) ? [{ field, operator: 'lte', value: `${numberValue}` }] : [];
      case FiltrationType.TODAY:
        return [
          { field, operator: 'gte', value: this.formatDateStart(moment()) },
          { field, operator: 'lte', value: this.formatDateEnd(moment()) }
        ];
      case FiltrationType.YESTERDAY:
        return [
          { field, operator: 'gte', value: this.formatDateStart(moment().subtract(1, 'day')) },
          { field, operator: 'lte', value: this.formatDateEnd(moment().subtract(1, 'day')) }
        ];
      case FiltrationType.TOMORROW:
        return [
          { field, operator: 'gte', value: this.formatDateStart(moment().add(1, 'day')) },
          { field, operator: 'lte', value: this.formatDateEnd(moment().add(1, 'day')) }
        ];
      case FiltrationType.THIS_WEEK:
        return [
          { field, operator: 'gte', value: this.formatDateStart(moment().startOf('isoWeek')) },
          { field, operator: 'lte', value: this.formatDateEnd(moment().endOf('isoWeek')) }
        ];
      case FiltrationType.THIS_MONTH:
        return [
          { field, operator: 'gte', value: this.formatDateStart(moment().startOf('month')) },
          { field, operator: 'lte', value: this.formatDateEnd(moment().endOf('month')) }
        ];
      case FiltrationType.THIS_YEAR:
        return [
          { field, operator: 'gte', value: this.formatDateStart(moment().startOf('year')) },
          { field, operator: 'lte', value: this.formatDateEnd(moment().endOf('year')) }
        ];
      case FiltrationType.NEXT_MONTH:
        return [
          { field, operator: 'gte', value: this.formatDateStart(moment().add(1, 'month').startOf('month')) },
          { field, operator: 'lte', value: this.formatDateEnd(moment().add(1, 'month').endOf('month')) }
        ];
      case FiltrationType.NEXT_YEAR:
        return [
          { field, operator: 'gte', value: this.formatDateStart(moment().add(1, 'year').startOf('year')) },
          { field, operator: 'lte', value: this.formatDateEnd(moment().add(1, 'year').endOf('year')) }
        ];
      case FiltrationType.LAST_WEEK:
        return [
          { field, operator: 'gte', value: this.formatDateStart(moment().subtract(1, 'week').startOf('isoWeek')) },
          { field, operator: 'lte', value: this.formatDateEnd(moment().subtract(1, 'week').endOf('isoWeek')) }
        ];
      case FiltrationType.LAST_7_DAYS:
        return [
          { field, operator: 'gte', value: this.formatDateStart(moment().subtract(6, 'day')) },
          { field, operator: 'lte', value: this.formatDateEnd(moment()) }
        ];
      case FiltrationType.LAST_MONTH:
        return [
          { field, operator: 'gte', value: this.formatDateStart(moment().subtract(1, 'month').startOf('month')) },
          { field, operator: 'lte', value: this.formatDateEnd(moment().subtract(1, 'month').endOf('month')) }
        ];
      case FiltrationType.LAST_YEAR:
        return [
          { field, operator: 'gte', value: this.formatDateStart(moment().subtract(1, 'year').startOf('year')) },
          { field, operator: 'lte', value: this.formatDateEnd(moment().subtract(1, 'year').endOf('year')) }
        ];
      default:
        return [];
    }
  }

  /**
   * The Status column shows a coarse grouping (e.g. "Execution in progress") that maps to several
   * ApprovalState values. Build the label -> codes map by inverting approvalStatusNaming so it
   * stays in sync with what the grid displays.
   */
  private statusGroupOptions(): { label: string; codes: string[] }[] {
    const groups = new Map<string, string[]>();
    (Object.values(ApprovalState) as ApprovalState[]).forEach(state => {
      const label = approvalStatusNaming(state);
      if (!label || label === '-') return;
      const codes = groups.get(label) ?? [];
      codes.push(`${state}`);
      groups.set(label, codes);
    });
    return Array.from(groups.entries()).map(([label, codes]) => ({ label, codes }));
  }

  private getColumnFiltersForRequest(): BackendColumnFilter[] | undefined {
    const filters: BackendColumnFilter[] = [];

    Object.entries(this.filters).forEach(([column, filter]) => {
      if (!filter) return;

      const field = this.mapSortField(column);
      if (!field) return;

      filters.push(...this.mapFilterToBackend(field, filter));
    });

    return filters.length > 0 ? filters : undefined;
  }

  private buildWaitingForCurrentUserFilter(): BackendColumnFilter {
    const emails = this.waitingForAssigneeEmails;
    const currentUserEmail = emails.length > 0 ? emails.join(',') : NO_MATCH_SENTINEL;

    return {
      field: 'WaitingFor',
      operator: 'eq',
      value: currentUserEmail
    };
  }

  private buildSearchRequest(searchText: string): GeneralTabSearchRequest {
    const sortBy = this.currentSortActive ? this.mapSortField(this.currentSortActive) : undefined;
    let columnFilters = this.getColumnFiltersForRequest() ?? [];

    if (this.showWaitingForOnly) {
      columnFilters.push(this.buildWaitingForCurrentUserFilter());
    }

    return {
      pageNumber: this.pageIndex + 1,
      pageSize: this.pageSize,
      filter: {
        reportType: undefined,
        search: searchText.trim() || undefined,
        statuses: this.getStatusesFilter(),
        statusFilterMode: this.getStatusFilterMode(),
        sortBy,
        sortDirection: this.getSortDirection(),
        columnFilters: columnFilters.length > 0 ? columnFilters : undefined
      }
    };
  }

  getStatusLabel(step: string): string {
    const option = ApprovalFlowStateOptions.find(opt => opt.value === step);
    return option ? option.label : step;
  }
  getStepLabel(step: string): string {
    const option = FDMStatusOptions.find(opt => opt.value === step);
    return option ? option.label : step;
  }
  getReportLabel(step: string): string {
    const option = ReportTypeOptions.find(opt => opt.value === step);
    return option ? option.label : step;
  }
  getConsequenceRankingLabel(step: string): string {
    const option = consequenceRankingOptions.find(opt => opt.value === step);
    return option ? option.label : step;
  }
  getFacilityLabel(step: string): string {
    const option = FacilityEnumOptions.find(opt => opt.value === step);
    return option ? option.label : step;
  }

  loadReports(searchText: string = ''): void {
    this._loaderService.startLoading();

    const request = this.buildSearchRequest(searchText);

    this.apiClient.search(ApiGeneralTabSearchRequest.fromJS(request))
      .pipe(
        first(),
        finalize(() => {
          this._loaderService.stopLoading();
          this.cdr.markForCheck();
        })
      )
      .subscribe((records: any) => {
        const totalCount = records.totalCount ?? 0;

        this.dataSource = records.items
          ? records.items.map((report: any) => {
            const reportAny = report as any;
            const closedOn = report.closedOn ? moment(report.closedOn).local().toDate() : undefined;
            const resolutionTargetDate = report.resolutionTargetDate
              ? moment(report.resolutionTargetDate).local().toDate()
              : undefined;
            const planApprovalDueDate = reportAny.planApprovalDueDate
              ? moment(reportAny.planApprovalDueDate).local().toDate()
              : undefined;
            const formattedReportType = ReportTypeOptions.find(item => item.value === report.reportType)?.label ?? null;
            const consequenceRanking = report.consequenceRanking === 'NotAssigned' ? 'Not Assigned' : report.consequenceRanking;
            const createdOn = utcToLocal(report.createdOn)?.toDate() ?? undefined;
            const waitingFor = report.status === ApprovalState.PlanApproved ? "Action item owner/owners" : report.waitingFor;
            const riskEvaluationScore = reportAny.riskEvaluationScore === '' ? undefined : reportAny.riskEvaluationScore;

            return {
              ...report,
              step: report.status ?? undefined,
              status: report.status ? approvalStatusNaming(report.status) : undefined,
              closedOn,
              createdOn,
              consequenceRanking,
              formattedReportType,
              resolutionTargetDate,
              waitingFor,
              equipment: reportAny.equipment,
              isItSafeguard: this.formatEquipmentSafeguard(reportAny.isSafeguard ?? reportAny.isItSafeguard),
              riskEvaluationScore,
              planApprovalDueDate,
              daysForApproval: reportAny.daysForApproval ?? undefined,
              owner: reportAny.owner ?? undefined,
              taRequired: this.asYesNo(reportAny.taRequired),
              ffsPerformed: this.formatFfsPerformed(reportAny.ffsPerformed),
              tlrReference: reportAny.tlrReference ?? undefined,
              tlrReferenceLink: reportAny.tlrLink ?? undefined,
              woReference: reportAny.woReference ?? undefined,
              woReferenceLink: reportAny.woLink ?? undefined,
              mocReference: reportAny.mocReference ?? undefined,
              mocReferenceLink: reportAny.mocLink ?? undefined,
              dewItReference: reportAny.dewItReference ?? undefined,
              dewItReferenceLink: reportAny.dewItLink ?? undefined,
              damageMechanismsFailureMode: reportAny.damageMechanisms ?? reportAny.damageMechanismsFailureMode ?? undefined,
            }
          })
          : [];

        this.totalPageCount = totalCount;
        this.filteredData = [...this.dataSource];
        this.paginatedData = [...this.dataSource];

        this.cdr.markForCheck();
      });
  }

  isDateColumn(column: string): boolean {
    return [
      'resolutionTargetDate',
      'closedOn',
      'createdOn',
      'planApprovedDate',
      'planApprovalDueDate',
    ].includes(column);
  }
  getCombinedColumns(): string[] {
    return ['select', ...this.displayedColumns];
    // return [...this.displayedColumns];
  }
  getColumnLabel(column: string): string {
    return this.columnLabels[column] || column;
  }
  isLogoColumn(column: string): boolean {
    return column === 'initiator';
  }

  isReferenceColumn(column: string): boolean {
    return ['tlrReference', 'woReference', 'mocReference', 'dewItReference'].includes(column);
  }

  getReferenceHref(report: GeneralTabRecord, column: string): string | undefined {
    if (column === 'tlrReference') return report.tlrReferenceLink;
    if (column === 'woReference') return report.woReferenceLink;
    if (column === 'mocReference') return report.mocReferenceLink;
    if (column === 'dewItReference') return report.dewItReferenceLink;
    return undefined;
  }

  getInitials(email: string | undefined): string {
    if (!email) return '';
    return email.slice(0, 2).toUpperCase();
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.paginatedData.length;
    return numRows > 0 && numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected() ?
      this.selection.clear() :
      this.paginatedData.forEach(row => this.selection.select(row));
  }

  onAddReport() {
    this.router.navigate(['reports/add']);
    this.store.restore();
    this.approvalFlow.reset();
  }

  // Custom pagination methods
  onPageChange(page: number): void {
    this.pageIndex = page - 1; // Convert to 0-based index
    const currentSearch = this.control.value || '';
    this.loadReports(currentSearch.trim());
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.pageIndex = 0; // Reset to first page when changing page size
    const currentSearch = this.control.value || '';
    this.loadReports(currentSearch.trim());
  }

  get totalPages(): number {
    const total = this.getTotalCount();
    return total > 0 ? Math.ceil(total / this.pageSize) : 1;
  }

  get currentPage(): number {
    return this.pageIndex + 1; // Convert to 1-based for display
  }

  getTotalCount(): number {
    return this.totalPageCount;
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const total = this.totalPages;
    const current = this.currentPage;

    if (total <= 7) {
      // Show all pages if 7 or fewer
      for (let i = 1; i <= total; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (current <= 3) {
        // Near the beginning
        pages.push(2, 3, 4, -1, total); // -1 represents ellipsis
      } else if (current >= total - 2) {
        // Near the end
        pages.push(-1, total - 3, total - 2, total - 1, total);
      } else {
        // In the middle
        pages.push(-1, current - 1, current, current + 1, -1, total);
      }
    }

    return pages;
  }

  goToFirstPage(): void {
    if (this.currentPage !== 1) {
      this.onPageChange(1);
    }
  }

  goToLastPage(): void {
    if (this.currentPage !== this.totalPages) {
      this.onPageChange(this.totalPages);
    }
  }

  goToPreviousPage(): void {
    if (this.currentPage > 1) {
      this.onPageChange(this.currentPage - 1);
    }
  }

  goToNextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.onPageChange(this.currentPage + 1);
    }
  }

  filters: Record<string, FilterResponse | null> = {};

  applyFilter(res: FilterResponse | null, column: string) {
    if (!this.isServerFilterableColumn(column)) {
      return;
    }

    this.pageIndex = 0;
    this.filters[column] = res;
    this.loadReports(this.control.value?.trim());
  }

  resetAllFilters() {
    this.pageIndex = 0;
    this.showDraftedOnly = false;
    this.showWaitingForOnly = false;
    this.filtersResetVersion += 1;

    this.filters = {};
    this.control.setValue('', { emitEvent: false });

    this.currentSortActive = '';
    this.currentSortDirection = '';

    if (this.sort) {
      this.sort.active = '';
      this.sort.direction = '';
    }

    this.selection.clear();
    this.searchReset$.next('');
  }

  isActive(column: string) {
    if (!this.isServerFilterableColumn(column)) {
      return false;
    }

    return this.filters[column] != null;
  }

  /**
   * Shows only report with statuses - Draft and Drafted for review
   */
  draftedDeviationReports() {
    this.pageIndex = 0;
    this.showDraftedOnly = true;
    this.showWaitingForOnly = false;
    this.loadReports(this.control.value?.trim());
  }

  /**
   * Default view
   * Show all except Drafted and drafted for review.
   */
  deviationReports() {
    this.pageIndex = 0;
    this.showDraftedOnly = false;
    this.showWaitingForOnly = false;
    this.loadReports(this.control.value?.trim());
  }

  waitingForReports() {
    this.pageIndex = 0;
    this.showDraftedOnly = false;
    this.showWaitingForOnly = true;
    this.loadReports(this.control.value?.trim());
  }
}
